import { gql } from '../../core/graphql-client.js';
import { auth } from '../../core/auth-store.js';
import { toast } from '../../core/event-bus.js';
import type { Person } from '../../types.js';

export class ProfilePage extends HTMLElement {
  private person: Person | null = null;

  connectedCallback(): void {
    this.innerHTML = '<loading-spinner text="Ładowanie..."></loading-spinner>';
    this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      this.person = await auth.fetchCurrentPerson('id firstName lastName displayName email phone isAdmin hasPassword');
      if (!this.person) {
        this.innerHTML = '<div class="page"><p>Nie znaleziono danych.</p></div>';
        return;
      }
      this.render();
    } catch (e) {
      this.innerHTML = `<div class="page"><p class="error-text">Błąd: ${e instanceof Error ? e.message : e}</p></div>`;
    }
  }

  private render(): void {
    const p = this.person!;
    const hasPassword = !!p.hasPassword;

    this.innerHTML = `
      <div class="page">
        <div class="page-header">
          <h1>Moje dane</h1>
        </div>
        <form id="profile-form" class="card">
          <div class="form-group">
            <label for="firstName">Imię</label>
            <input id="firstName" type="text" value="${p.firstName}" disabled>
          </div>
          <div class="form-group">
            <label for="lastName">Nazwisko</label>
            <input id="lastName" type="text" value="${p.lastName}" disabled>
          </div>
          <div class="form-group">
            <label for="email">Email</label>
            <input id="email" type="email" value="${p.email ?? ''}" placeholder="twój@email.pl">
          </div>
          <div class="form-group">
            <label for="phone">Telefon</label>
            <input id="phone" type="tel" value="${p.phone ?? ''}" placeholder="+48...">
          </div>
          <button type="submit" class="btn btn-primary profile-save">Zapisz</button>
        </form>

        <div class="card" style="margin-top:var(--space-lg);">
          <h2 style="margin:0 0 var(--space-sm);">${hasPassword ? 'Zmień hasło' : 'Ustaw hasło'}</h2>
          <p class="profile-password-hint">${hasPassword
            ? 'Podaj aktualne hasło i nowe, aby je zmienić.'
            : 'Ustaw hasło, aby logować się bezpośrednio emailem i hasłem.'
          }</p>

          ${hasPassword ? `
          <div class="form-group">
            <label for="current-password">Aktualne hasło</label>
            <input id="current-password" type="password">
          </div>` : ''}

          <div class="form-group">
            <label for="new-password">Nowe hasło</label>
            <input id="new-password" type="password" placeholder="Min. 8 znaków">
          </div>
          <div class="form-group">
            <label for="confirm-password">Potwierdź hasło</label>
            <input id="confirm-password" type="password">
          </div>
          <button id="save-password" class="btn btn-primary profile-save">${hasPassword ? 'Zmień hasło' : 'Ustaw hasło'}</button>
        </div>
      </div>
      <style>
        .profile-save { width: 100%; margin-top: var(--space-md); }
        .profile-password-hint { font-size: .85rem; color: var(--color-on-surface-muted); margin: 0 0 var(--space-md); }
      </style>
    `;

    this.querySelector('#profile-form')!.addEventListener('submit', (e) => {
      e.preventDefault();
      this.save();
    });

    this.querySelector('#save-password')!.addEventListener('click', () => {
      this.savePassword(hasPassword);
    });
  }

  private async save(): Promise<void> {
    const email = (this.querySelector('#email') as HTMLInputElement).value.trim();
    const phone = (this.querySelector('#phone') as HTMLInputElement).value.trim();

    try {
      if (auth.isImpersonating) {
        await gql.query(`
          mutation($id: Int!, $input: UpdatePersonInput!) {
            updatePerson(id: $id, input: $input) { id }
          }
        `, { id: auth.effectivePersonId, input: { email: email || null, phone: phone || null } });
      } else {
        await gql.query(`
          mutation($email: String, $phone: String) {
            updateMe(email: $email, phone: $phone) { id }
          }
        `, { email: email || null, phone: phone || null });
      }
      toast('Dane zapisane', 'success');
    } catch (e) {
      toast(`Błąd: ${e instanceof Error ? e.message : e}`, 'error');
    }
  }

  private async savePassword(hasExisting: boolean): Promise<void> {
    const newPw = (this.querySelector('#new-password') as HTMLInputElement).value;
    const confirmPw = (this.querySelector('#confirm-password') as HTMLInputElement).value;

    if (newPw !== confirmPw) {
      toast('Hasła nie są zgodne', 'error');
      return;
    }
    if (newPw.length < 8) {
      toast('Hasło musi mieć co najmniej 8 znaków', 'error');
      return;
    }

    const currentPw = hasExisting
      ? (this.querySelector('#current-password') as HTMLInputElement).value
      : null;

    if (hasExisting && !currentPw) {
      toast('Podaj aktualne hasło', 'error');
      return;
    }

    try {
      await gql.query(
        `mutation($currentPassword: String, $newPassword: String!) {
          setMyPassword(currentPassword: $currentPassword, newPassword: $newPassword)
        }`,
        { currentPassword: currentPw, newPassword: newPw },
      );
      toast('Hasło zapisane', 'success');
      (this.querySelector('#new-password') as HTMLInputElement).value = '';
      (this.querySelector('#confirm-password') as HTMLInputElement).value = '';
      if (hasExisting) (this.querySelector('#current-password') as HTMLInputElement).value = '';
    } catch (e) {
      toast(`Błąd: ${e instanceof Error ? e.message : e}`, 'error');
    }
  }
}

customElements.define('profile-page', ProfilePage);
